/* ==========================================================
   VPP / Lumia — FACETS FALLBACK (FULL RELOAD) + PRICE SUPPORT
   - Category/Availability/etc work via URL navigation
   - Adds robust PRICE handling:
     • listens to input for price number fields + range sliders
     • supports Apply button via form submit
     • restores <price-range> min/max syncing
   ========================================================== */

(function () {
  // ---- debounce fallback ----
  const debounce =
    window.debounce ||
    function (fn, wait) {
      let t;
      return function () {
        const args = arguments;
        clearTimeout(t);
        t = setTimeout(() => fn.apply(this, args), wait);
      };
    };

  function isFacetForm(form) {
    if (!form) return false;
    return (
      form.id === "FacetFiltersForm" ||
      form.id === "FacetFiltersFormMobile" ||
      form.closest("facet-filters-form")
    );
  }

  function isPriceInput(el) {
    if (!el) return false;
    const name = (el.getAttribute("name") || "").toLowerCase();

    // Shopify price params usually look like:
    // filter.v.price.gte / filter.v.price.lte (or similar)
    if (name.includes("filter.v.price")) return true;
    if (name.includes("filter.p.price")) return true;

    // Lumia sometimes wraps price inputs in <price-range>
    if (el.closest && el.closest("price-range")) return true;

    return false;
  }

  function buildSearchParams(fromForm) {
    const fd = new FormData(fromForm);
    const sp = new URLSearchParams();

    for (const [k, v] of fd.entries()) {
      const val = String(v ?? "").trim();
      if (!val) continue;
      sp.append(k, val);
    }

    // Preserve/attach sort_by if it exists
    const sort = document.querySelector('[name="sort_by"]');
    if (sort && !sp.has("sort_by") && sort.value) {
      sp.set("sort_by", sort.value);
    }

    return sp;
  }

  function go(fromForm) {
    if (!fromForm) return;
    const sp = buildSearchParams(fromForm);
    const qs = sp.toString();
    const url = qs ? `${window.location.pathname}?${qs}` : window.location.pathname;
    window.location.assign(url);
  }

  const goDebounced = debounce(go, 300);

  /* -----------------------------
     1) INPUT handler
     - normal filters (checkbox/radio/select) handled here
     - price: allow number/range input events to trigger navigation
  -------------------------------- */
  document.addEventListener(
    "input",
    function (e) {
      const t = e.target;
      if (!t) return;

      // ignore search fields inside facet UI (category search input)
      if (t.classList && t.classList.contains("search")) return;

      const form = t.form || (t.closest && t.closest("form"));
      if (!isFacetForm(form)) return;

      // PRICE: range slider should navigate on input
      if (t.type === "range" && isPriceInput(t)) {
        goDebounced(form);
        return;
      }

      // PRICE: number inputs often only emit input events while typing
      if (t.type === "number" && isPriceInput(t)) {
        goDebounced(form);
        return;
      }

      // Other inputs:
      // - Let change event handle number/text to avoid reload on every keystroke
      if (t.type === "number" || t.type === "text") return;

      goDebounced(form);
    },
    true
  );

  /* -----------------------------
     2) CHANGE handler
     - catches checkbox/select changes
     - also catches price number fields after blur in some browsers
  -------------------------------- */
  document.addEventListener(
    "change",
    function (e) {
      const t = e.target;
      if (!t) return;

      const form = t.form || (t.closest && t.closest("form"));
      if (!isFacetForm(form)) return;

      go(form);
    },
    true
  );

  /* -----------------------------
     3) ENTER on number input (price)
  -------------------------------- */
  document.addEventListener(
    "keypress",
    function (e) {
      const t = e.target;
      if (!t) return;

      if (t.type !== "number") return;
      if (e.key !== "Enter") return;

      const form = t.form || (t.closest && t.closest("form"));
      if (!isFacetForm(form)) return;

      e.preventDefault();
      go(form);
    },
    true
  );

  /* -----------------------------
     4) SUBMIT handler (Apply button)
     - If theme prevents normal submit, we force navigation
  -------------------------------- */
  document.addEventListener(
    "submit",
    function (e) {
      const form = e.target;
      if (!isFacetForm(form)) return;
      e.preventDefault();
      go(form);
    },
    true
  );

  /* -----------------------------
     5) Active facet remove links
  -------------------------------- */
  document.addEventListener(
    "click",
    function (e) {
      const a =
        e.target &&
        e.target.closest &&
        e.target.closest(".js-facet-remove a, facet-remove a");
      if (!a || !a.href) return;

      e.preventDefault();
      window.location.assign(a.href);
    },
    true
  );

  /* -----------------------------
     6) Sort dropdown
  -------------------------------- */
  document.addEventListener(
    "change",
    function (e) {
      const t = e.target;
      if (!t || t.name !== "sort_by") return;

      const form =
        document.getElementById("FacetFiltersForm") ||
        document.getElementById("FacetFiltersFormMobile") ||
        t.form;

      if (!form) {
        const sp = new URLSearchParams(window.location.search);
        sp.set("sort_by", t.value);
        window.location.assign(`${window.location.pathname}?${sp.toString()}`);
        return;
      }

      go(form);
    },
    true
  );

  /* ==========================================================
     Restore <price-range> min/max syncing (lightweight)
     ========================================================== */
  class PriceRange extends HTMLElement {
    constructor() {
      super();
      this.inputs = Array.from(this.querySelectorAll("input"));

      this.onAnyInput = debounce(() => this.setMinAndMaxValues(), 50);

      this.inputs.forEach((el) => {
        el.addEventListener("input", this.onAnyInput);
        el.addEventListener("change", this.onAnyInput);
      });

      this.setMinAndMaxValues();
    }

    setMinAndMaxValues() {
      if (!this.inputs || this.inputs.length < 2) return;

      const minInput = this.inputs[0];
      const maxInput = this.inputs[1];

      if (!minInput || !maxInput) return;

      if (maxInput.value) minInput.setAttribute("max", maxInput.value);
      if (minInput.value) maxInput.setAttribute("min", minInput.value);
      if (minInput.value === "") maxInput.setAttribute("min", 0);
      if (maxInput.value === "") minInput.setAttribute("max", minInput.getAttribute("max") || "");
    }
  }

  if (!customElements.get("price-range")) {
    customElements.define("price-range", PriceRange);
  }
})();

/* ==========================================================
   VPP / Lumia — PRICE SLIDER -> FACET FORM EVENT BRIDGE
   Works with your "FACETS FALLBACK (FULL RELOAD)"
   ========================================================== */
(function () {
  function getFacetForm(el) {
    return (
      el.closest("facet-filters-form")?.querySelector("form") ||
      el.closest("form")
    );
  }

  function isFacetForm(form) {
    if (!form) return false;
    return (
      form.id === "FacetFiltersForm" ||
      form.id === "FacetFiltersFormMobile" ||
      form.closest("facet-filters-form")
    );
  }

  // Debounce so dragging doesn't trigger 200 navigations
  function debounce(fn, wait) {
    let t;
    return function () {
      const args = arguments;
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), wait);
    };
  }

  const fireFacetUpdate = debounce((form) => {
    if (!form) return;
    try { form.dispatchEvent(new Event("input", { bubbles: true })); } catch (e) {}
    try { form.dispatchEvent(new Event("change", { bubbles: true })); } catch (e) {}
  }, 120);

  document.addEventListener(
    "input",
    function (e) {
      const t = e.target;
      if (!t || !t.closest) return;

      const slider = t.closest("price-slider");
      if (!slider) return;

      const form = getFacetForm(slider);
      if (!isFacetForm(form)) return;

      if (t.tagName === "INPUT" && t.type === "range") {
        fireFacetUpdate(form);
      }
    },
    true
  );

  document.addEventListener(
    "change",
    function (e) {
      const t = e.target;
      if (!t || !t.closest) return;

      const slider = t.closest("price-slider");
      if (!slider) return;

      const form = getFacetForm(slider);
      if (!isFacetForm(form)) return;

      if (t.tagName === "INPUT" && (t.type === "range" || t.type === "number")) {
        fireFacetUpdate(form);
      }
    },
    true
  );

  document.addEventListener(
    "keydown",
    function (e) {
      if (e.key !== "Enter") return;
      const t = e.target;
      if (!t || !t.closest) return;

      const slider = t.closest("price-slider");
      if (!slider) return;

      const form = getFacetForm(slider);
      if (!isFacetForm(form)) return;

      if (t.tagName === "INPUT" && (t.type === "number" || t.type === "text")) {
        fireFacetUpdate(form);
      }
    },
    true
  );
})();

/* ==========================================================
   VPP FIX — RESTORE LUMIA "ViewMode" (products per row)
   This ONLY restores the grid-per-row switcher behavior.
   ========================================================== */
(function () {
  // Safe helper: remove classes from element that end with suffix (e.g. "_xl")
  if (typeof window.removeClassBySuffix !== "function") {
    window.removeClassBySuffix = function (el, suffix) {
      if (!el || !el.classList || !suffix) return;
      Array.from(el.classList).forEach((cls) => {
        if (cls.endsWith(suffix)) el.classList.remove(cls);
      });
    };
  }

  function getGridEl() {
    return (
      document.querySelector("[data-product-grid]") ||
      document.getElementById("product-grid") ||
      document.querySelector("#ProductGridContainer [id*='product-grid']") ||
      null
    );
  }

  class ViewMode extends HTMLElement {
    constructor() {
      super();
      this.grid = getGridEl();
      if (!this.grid) return;

      this.querySelectorAll("[data-view]").forEach((button) => {
        button.addEventListener("click", this.onButtonClick.bind(this));
      });
    }

    onButtonClick(e) {
      e.preventDefault();
      const target = e.currentTarget;
      if (!target) return;
      if (!target.classList.contains("active")) {
        this.setPerRow(target);
      }
    }

    setSizes() {
      // Keep original behavior, but guard if pieces are missing
      const sizes = [
        0,
        0,
        "(min-width: 1400px) 559px, (min-width: 1200px) 540px, (min-width: 992px) 450px, calc((100vw - 30px - 20px) / 2)",
        "(min-width: 1400px) 362px, (min-width: 1200px) 350px, (min-width: 992px) 289px, (min-width: 768px) 210px, calc((100vw - 30px - 20px) / 2)",
        "(min-width: 1400px) 264px, (min-width: 1200px) 255px, (min-width: 992px) 210px, (min-width: 768px) 150px, calc((100vw - 30px - 20px) / 2)",
        "(min-width: 1400px) 205px, (min-width: 1200px) 198px, (min-width: 992px) 162px, (min-width: 768px) 210px, calc((100vw - 30px - 20px) / 2)",
      ];

      const currentView = this.getCurrentBreakpointActive();
      if (!currentView) return;

      const view = currentView.dataset.view || "";
      const parts = view.split("_");
      const n = parseInt(parts[parts.length - 1], 10);
      if (!Number.isFinite(n)) return;

      const grid = this.grid || getGridEl();
      if (!grid) return;

      const imgs = grid.querySelectorAll("product-card .prd-image img");
      imgs.forEach((img) => {
        if (sizes[n]) img.sizes = sizes[n];
      });
    }

    setPerRow(el) {
      const grid = this.grid || getGridEl();
      if (!grid) return;

      // Clear active within the current breakpoint group
      if (el.closest(".view-mode--xxl")) {
        this.querySelectorAll(".view-mode--xxl [data-view]").forEach((x) => x.classList.remove("active"));
        window.removeClassBySuffix(grid, "_xxl");
      } else if (el.closest(".view-mode--xl")) {
        this.querySelectorAll(".view-mode--xl [data-view]").forEach((x) => x.classList.remove("active"));
        window.removeClassBySuffix(grid, "_xl");
      } else if (el.closest(".view-mode--md")) {
        this.querySelectorAll(".view-mode--md [data-view]").forEach((x) => x.classList.remove("active"));
        window.removeClassBySuffix(grid, "_md");
      } else {
        this.querySelectorAll(".view-mode--sm [data-view]").forEach((x) => x.classList.remove("active"));
        window.removeClassBySuffix(grid, "_sm");
      }

      el.classList.add("active");
      grid.classList.add(el.dataset.view);

      grid.querySelectorAll("product-card").forEach((pc) => {
        pc.productWidth?.();
        if (el.dataset.view && el.dataset.view.includes("list")) pc.loadAjax?.();
      });

      this.setSizes();
    }

    getCurrentBreakpoint() {
      let current;
      this.querySelectorAll(".view-mode").forEach((el) => {
        const display = (el.currentStyle ? el.currentStyle.display : getComputedStyle(el, null).display);
        if ((display === "flex" || display === "block") && el.querySelectorAll(".active").length > 0) {
          current = el;
        }
      });
      return current;
    }

    getCurrentBreakpointActive() {
      const bp = this.getCurrentBreakpoint();
      return bp ? bp.querySelector(".active") : false;
    }
  }

  function defineOnce() {
    if (window.customElements && !customElements.get("view-mode")) {
      customElements.define("view-mode", ViewMode);
    }
  }

  // Define immediately if possible
  defineOnce();

  // Also re-define in case this file loads before customElements is ready
  document.addEventListener("DOMContentLoaded", defineOnce);
})();
